import { Icon, Range, stopEvent, View } from '@fold-dev/core'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { formatAdaptiveTimestamp, formatTimestamp, formatTimestampShort } from '../../helpers'
import { flattenSectionsTasks } from '../tasks'
import { TbArrowLeft, TbArrowRight } from 'react-icons/tb'

const baseTimeOffset = new Date().getTime() / 1000
const itemHeight = 40
const headerHeight = 30
const offsetChunk = 50

const ArrowRight = ({ top, left }) => {
    return (
        <div
            className="f-row timeline-indicator"
            style={{
                top: top + 4,
                left: left - 4,
                height: itemHeight - 9,
                transform: 'translateX(-100%)',
            }}>
            <Icon
                icon={TbArrowRight}
                size="sm"
            />
        </div>
    )
}

const ArrowLeft = ({ top, left }) => {
    return (
        <div
            className="f-row timeline-indicator"
            style={{
                top: top + 4,
                left: left + 4,
                height: itemHeight - 9,
            }}>
            <Icon
                icon={TbArrowLeft}
                size="sm"
            />
        </div>
    )
}

const TimelineItem = ({ item, index, left, top, width, height, onMouseDown, secondsPerSlot, dragging }) => {
    const { start, end, title } = item
    const ref = useRef(null)

    return (
        <div
            ref={ref}
            onMouseDown={onMouseDown(index, 'move')}
            className="timeline-item"
            style={{
                top,
                left,
                width,
                height,
                borderRadius: 'var(--f-radius-full)',
                cursor: dragging ? 'grabbing' : 'pointer',
            }}>
            <div
                className="f-ellipsis f-row"
                style={{ gap: 5 }}>
                <div
                    className="f-ellipsis f-text sm"
                    dangerouslySetInnerHTML={{ __html: title }}
                />
                <div
                    className="f-ellipsis f-text sm"
                    style={{ color: 'var(--f-color-text-weakest)' }}>
                    {formatTimestampShort(start * secondsPerSlot + baseTimeOffset)} -{' '}
                    {formatTimestampShort(end * secondsPerSlot + baseTimeOffset)}
                </div>
            </div>

            <div
                className="timeline-resize-handle is-left"
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '6px',
                    height: '100%',
                    cursor: 'ew-resize',
                    zIndex: 2,
                }}
                onMouseDown={onMouseDown(index, 'resize-left')}
            />
            <div
                className="timeline-resize-handle is-right"
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    width: '6px',
                    height: '100%',
                    cursor: 'ew-resize',
                    zIndex: 2,
                }}
                onMouseDown={onMouseDown(index, 'resize-right')}
            />
        </div>
    )
}

export const Timeline = ({ tasks, sections, teamId, date, onTaskUpdate, onTaskOpen }) => {
    const [items, setItems] = useState([])
    const [width, setWidth] = useState(1850)
    const [slots, setSlots] = useState(100)
    const [offset, setOffset] = useState(0)
    const [headerSlotsDivider, setHeaderSlotsDivider] = useState(10)
    const [secondsPerSlot, setSecondsPerSlot] = useState((60 * 60 * 24) / 2)
    const [scrollLeft, setScrollLeft] = useState(0)

    const sliderRange = useMemo(() => {
        const margin = 200
        return {
            min: Math.max(0, offset - margin),
            max: slots + margin,
        }
    }, [offset, slots])

    const length = useMemo(() => width / slots, [width, slots])
    const height = useMemo(() => items.length * itemHeight, [items, itemHeight])

    const [dragging, setDragging] = useState(false)

    const scrollCorrectionRef = useRef(0)
    const containerRef = useRef(null)
    const dragState = useRef(null)

    const updateCache = useRef(null)
    const firstLoad = useRef(false)
    const clickStartRef = useRef({ x: 0, y: 0, time: 0 })

    const [ghostItem, setGhostItem] = useState(null)
    const ghostStateRef = useRef(null)

    const handleZoom = (e) => {
        const baseSlots = 150 // the starting slot value
        const newSlots = parseInt(e.target.value)
        const newDivider = Math.max(1, Math.round((newSlots / baseSlots) * 10))

        setSlots(newSlots)
        setHeaderSlotsDivider(newDivider)
    }

    const onMouseDownItemRow = (item, index) => (e) => {
        stopEvent(e)

        const rect = containerRef.current.getBoundingClientRect()
        const startX = e.clientX - rect.left + containerRef.current.scrollLeft
        const startSlot = Math.floor(startX / length) - offset

        ghostStateRef.current = {
            id: item.id,
            index,
            startSlot,
            endSlot: startSlot,
        }

        setGhostItem({ id: item.id, start: startSlot, end: startSlot })

        document.addEventListener('mousemove', onGhostMouseMove)
        document.addEventListener('mouseup', onGhostMouseUp)
    }

    const onGhostMouseMove = (e) => {
        if (!ghostStateRef.current) return

        const rect = containerRef.current.getBoundingClientRect()
        const currentX = e.clientX - rect.left + containerRef.current.scrollLeft
        const currentSlot = Math.floor(currentX / length) - offset

        const { startSlot } = ghostStateRef.current
        const start = Math.min(startSlot, currentSlot)
        const end = Math.max(startSlot, currentSlot)

        ghostStateRef.current.endSlot = currentSlot
        setGhostItem({ ...ghostStateRef.current, start, end })
    }

    const onGhostMouseUp = () => {
        document.removeEventListener('mousemove', onGhostMouseMove)
        document.removeEventListener('mouseup', onGhostMouseUp)

        if (!ghostStateRef.current) return

        let { id, startSlot, endSlot } = ghostStateRef.current

        if (startSlot > endSlot) [startSlot, endSlot] = [endSlot, startSlot]

        const startTimestamp = startSlot * secondsPerSlot + baseTimeOffset
        const endTimestamp = endSlot * secondsPerSlot + baseTimeOffset

        onTaskUpdate({
            id,
            start: new Date(startTimestamp * 1000),
            end: new Date(endTimestamp * 1000),
        })

        setGhostItem(null)
        ghostStateRef.current = null
    }

    const onMouseDown = (index, type: 'move' | 'resize-left' | 'resize-right') => (e) => {
        stopEvent(e)

        clickStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            time: Date.now(),
        }

        dragState.current = {
            index,
            type,
            startX: e.clientX,
            original: { ...items[index] },
        }

        setDragging(true)

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }

    const onMouseMove = (e) => {
        if (!dragState.current) return

        const { index, type, original } = dragState.current
        const deltaPx = e.clientX - dragState.current.startX
        const deltaSlots = Math.round(deltaPx / length)
        const updated = [...items]
        const item = { ...original }

        if (type === 'move') {
            item.start = Math.round(item.start + deltaSlots)
            item.end = Math.round(item.end + deltaSlots)
        } else if (type === 'resize-left') {
            item.start = Math.round(item.start + deltaSlots)
            if (item.start >= item.end) item.start = item.end - 1
        } else if (type === 'resize-right') {
            item.end = Math.round(item.end + deltaSlots)
            if (item.end <= item.start) item.end = item.start + 1
        }

        // no snapping!

        // if (type === 'move') {
        //     item.start += deltaSlots
        //     item.end += deltaSlots
        // } else if (type === 'resize-left') {
        //     item.start += deltaSlots
        //     if (item.start >= item.end) item.start = item.end - 1
        // } else if (type === 'resize-right') {
        //     item.end += deltaSlots
        //     if (item.end <= item.start) item.end = item.start + 1
        // }

        updated[index] = item
        setItems(updated)
        updateCache.current = item
    }

    const onMouseUp = (e) => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)

        const { x, y, time } = clickStartRef.current
        const deltaX = Math.abs(e.clientX - x)
        const deltaY = Math.abs(e.clientY - y)
        const deltaTime = Date.now() - time

        if (deltaX < 5 && deltaY < 5 && deltaTime < 200) {
            const { index } = dragState.current || {}
            onTaskOpen(tasks.find((task) => task.id == items[index].id))
            setDragging(false)
            dragState.current = null
            return
        }

        setDragging(false)

        dragState.current = null

        const { id, start, end } = updateCache.current || {}
        const startTimestamp = start * secondsPerSlot + baseTimeOffset
        const endTimestamp = end * secondsPerSlot + baseTimeOffset

        onTaskUpdate({
            id,
            start: new Date(startTimestamp * 1000),
            end: new Date(endTimestamp * 1000),
        })
    }

    const loadRight = () => {
        const newSlots = slots + offsetChunk
        setSlots(newSlots)
        setWidth(newSlots * length)
    }

    const loadLeft = () => {
        const added = offsetChunk
        const newSlots = slots + added
        const newOffset = offset + added
        const newWidth = newSlots * length

        scrollCorrectionRef.current = added * length

        setSlots(newSlots)
        setOffset(newOffset)
        setWidth(newWidth)
    }

    const handleScroll = (e) => {
        const el = e.currentTarget
        const scrollLeft = el.scrollLeft
        const maxScrollLeft = el.scrollWidth - el.clientWidth
        const tolerance = 1

        setScrollLeft(scrollLeft)

        if (scrollLeft >= maxScrollLeft - tolerance) loadRight()
        if (scrollLeft <= tolerance) loadLeft()
    }

    useLayoutEffect(() => {
        if (scrollCorrectionRef.current !== 0 && containerRef.current) {
            containerRef.current.scrollLeft += scrollCorrectionRef.current
            scrollCorrectionRef.current = 0
        }
    }, [slots, offset, width])

    useEffect(() => {
        const toSlot = (timestamp) => (timestamp - baseTimeOffset) / secondsPerSlot

        setItems(
            flattenSectionsTasks(sections).map(({ start, end, ...rest }) => ({
                start: start ? toSlot(new Date(start).getTime() / 1000) : null,
                end: end ? toSlot(new Date(end).getTime() / 1000) : null,
                ...rest,
            }))
        )

        if (!firstLoad.current) {
            setTimeout(loadLeft)
            // setTimeout(() => {
            //     containerRef.current.scrollLeft -= containerRef.current.offsetWidth / 5
            // }, 100)
        }

        firstLoad.current = true
    }, [sections, secondsPerSlot])

    useEffect(() => {
        const handleWheel = (e) => {
            if (e.shiftKey) {
                e.preventDefault()
                containerRef.current.scrollLeft += e.deltaY
            }
        }
        containerRef.current.addEventListener('wheel', handleWheel, { passive: false })
        return () => containerRef.current?.removeEventListener('wheel', handleWheel)
    }, [])

    return (
        <div className="timeline-root">
            <div className="timeline-container">
                <div className="timeline-scroll-area">
                    <div className="timeline-scroll-area-inner">
                        <div className="timeline-scroll-area-inner-container">
                            <div
                                className="timeline-scroll-area-scroll-container"
                                id="scroll-container"
                                ref={containerRef}
                                onScroll={handleScroll}>
                                <div
                                    className="timeline-header-container"
                                    style={{
                                        width,
                                        height: headerHeight,
                                    }}>
                                    {new Array(slots).fill(null).map((_, i) => {
                                        if (i % headerSlotsDivider !== 0) return null

                                        const accented = i - offset == 0

                                        return (
                                            <div
                                                key={i}
                                                className="timeline-header"
                                                style={{
                                                    width: length * headerSlotsDivider,
                                                    height: headerHeight,
                                                    top: 0,
                                                    left: i * length,
                                                }}>
                                                <span
                                                    style={{
                                                        fontWeight: accented ? 'bold' : undefined,
                                                        color: accented ? 'var(--f-color-accent)' : undefined,
                                                    }}
                                                    className="f-ellipsis">
                                                    {formatAdaptiveTimestamp(
                                                        (i - offset) * secondsPerSlot + baseTimeOffset,
                                                        secondsPerSlot,
                                                        slots
                                                    )}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>

                                <div
                                    style={{
                                        width,
                                        height,
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}>
                                    <div
                                        style={{
                                            width,
                                            height,
                                            top: 0,
                                            zIndex: 5,
                                            position: 'relative',
                                            overflow: 'hidden',
                                        }}>
                                        {items.map((item, index) => {
                                            if (item.start == null || item.end == null) return null

                                            const itemLeft = (item.start + offset) * length
                                            const itemWidth = (item.end - item.start) * length
                                            const itemTop = itemHeight * index + 6
                                            const itemRight = itemLeft + itemWidth

                                            const isLeftOffscreen = itemRight < scrollLeft
                                            const isRightOffscreen =
                                                itemLeft > scrollLeft + containerRef.current.offsetWidth

                                            return (
                                                <React.Fragment key={index}>
                                                    <TimelineItem
                                                        index={index}
                                                        item={item}
                                                        height={itemHeight - 13}
                                                        dragging={dragging}
                                                        secondsPerSlot={secondsPerSlot}
                                                        onMouseDown={onMouseDown}
                                                        left={itemLeft}
                                                        top={itemTop}
                                                        width={itemWidth}
                                                    />
                                                    {isLeftOffscreen && (
                                                        <ArrowLeft
                                                            left={scrollLeft}
                                                            top={itemHeight * index}
                                                        />
                                                    )}
                                                    {isRightOffscreen && (
                                                        <ArrowRight
                                                            left={scrollLeft + containerRef.current.offsetWidth}
                                                            top={itemHeight * index}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}

                                        {typeof ghostItem?.index == 'number' && (
                                            <div
                                                className="timeline-ghost"
                                                style={{
                                                    opacity: 0.5,
                                                    top: itemHeight * ghostItem.index + 6,
                                                    left: (ghostItem.start + offset) * length,
                                                    width: (ghostItem.end - ghostItem.start) * length,
                                                    height: itemHeight - 13,
                                                    borderRadius: 'var(--f-radius-full)',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        )}
                                        <div
                                            style={{
                                                overflow: 'hidden',
                                                position: 'absolute',
                                                zIndex: 3,
                                                width,
                                                height,
                                            }}>
                                            {items.map((item, index) => {
                                                return (
                                                    <div
                                                        key={index}
                                                        className="timeline-slot-line"
                                                        onMouseDown={onMouseDownItemRow(item, index)}
                                                        style={{
                                                            top: itemHeight * index,
                                                            width,
                                                            height: itemHeight,
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>

                                    {new Array(slots).fill(null).map((_, i) => (
                                        <div
                                            key={i}
                                            className="timeline-slot slot"
                                            style={{
                                                height,
                                                width: length,
                                                left: i * length,
                                                borderLeft:
                                                    i - offset == 0
                                                        ? '1px solid rgba(from var(--f-color-accent) r g b / 25%)'
                                                        : '1px solid rgba(from var(--f-color-surface-strong) r g b / 50%)',
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="timeline-footer">
                            <Range
                                min={sliderRange.min}
                                max={sliderRange.max}
                                step={1}
                                value={slots}
                                style={{ width: '100%' }}
                                onChange={handleZoom}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
